import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { Button as MUIButton } from '@mui/material';
import Page1 from './pages/Page1';
import Page2 from './pages/Page2';
import Page3 from './pages/Page3';
import { imeiValidation, fetchEstimatedPrice } from 'api/trade_in';
import { STORAGEKEY_TRADE_IN_FORM_DATA } from 'config/constants';
import styles from './TradeInFormModal.module.scss';
import { notifType, notify } from 'utils/notifSender';
import * as FS from '@fullstory/browser';
import { storeUserEvent } from 'api/user';
import { isSeptPromoValid } from 'utils/helpers';

const TradeInFormModal = ({ showTradeInModal: modal, toggleTradeInModal: toggle, selectedDurationTime, setTradeInDevice: localStorageSetTradeInDevice, product, subtotal, overtrade, maxTradeInValue = 925 }) => {
    const titles = {
        1: 'Select your device',
        2: 'Is your device in good condition?',
        3: 'Your estimated trade-in value has been calculated'
    };
    const [page, setPage] = useState(1);
    const [tradeInDevice, setTradeInDevice] = useState();
    const [tradeInDeviceIMEI, setTradeInDeviceIMEI] = useState('');
    const [deviceConditionString, setDeviceConditionString] = useState('');
    const [roughEstimate, setRoughEstimate] = useState('');
    const [productAssessmentFormCompleted, setProductAssessmentFormCompleted] = useState(false);
    const [isTncChecked, setIsTncChecked] = useState(false);
    const [page1Values, setPage1Values] = useState({
        selectedCategory: 'Smartphone',
        selectedBrand: '',
        selectedDevice: '',
        selectedStorage: ''
    });
    const [questionnaireAnswers, setQuestionnaireAnswers] = useState();

    const toPreviousPage = () => page > 1 ? setPage(page - 1) : toggle({ submitted: false });

    const proceed = () => setPage(page + 1);

    const logUserEvent = async (data, step = page) => {
        const event = {
            1: 'selected_device',
            2: 'device_condition',
            3: 'entered_imei',
            4: 'submitted_trade_in'
        };

        FS.event(`Trade-in: Form Modal - ${event[step]}`, {
            product_id: product?.id
        });

        storeUserEvent({
            event: `trade_in_form_modal_${event[step]}`,
            product_id: product?.id,
            ...data
        });
    };

    const toNextPage = () => {
        switch (page) {
            case 1:
                logUserEvent({
                    compasia_product_id: tradeInDevice.id,
                    brand: page1Values.selectedBrand,
                    category: page1Values.selectedCategory,
                    device_name: page1Values.selectedDevice
                });

                return proceed();
            case 2:
                logUserEvent({ device_condition_string: deviceConditionString });

                return getEstimatedPrice(true);
            case 3:
                return validateIMEI();
            default:
                break;
        }
    };

    const validateIMEI = () => {
        // if (!tradeInDevice.id, !tradeInDeviceIMEI) return console.log('Trade-in device or IMEI is missing');

        imeiValidation(tradeInDevice.id, tradeInDeviceIMEI)
            .then((res) => {
                const validIMEI = res.status === 'Success';

                if (validIMEI) {
                    logUserEvent({ imei_number: tradeInDeviceIMEI });
                    getEstimatedPrice();
                } else {
                    notify(res.msg, notifType.DANGER);
                }
            }).catch((err) => {
                console.error('Error validating IMEI:', err);
            });
    };

    const getEstimatedPrice = (isFetchRoughEstimate = false) => {
        fetchEstimatedPrice(tradeInDevice.id, tradeInDeviceIMEI, deviceConditionString).then((res) => {
            if (isFetchRoughEstimate) {
                setRoughEstimate(res.total);
                proceed();
            } else {
                logUserEvent({
                    ca_estimate_object: res,
                    trade_in_device: tradeInDevice
                }, 4);
                handleSubmit(res);
            }
        }).catch((err) => {
            console.error('Error fetching estimated price:', err);
        });
    };

    const disableCta = () => {
        if (page === 1 && !tradeInDevice) return true;
        if (page === 2 && !productAssessmentFormCompleted) return true;
        if (page === 3 && (!tradeInDeviceIMEI || !isTncChecked)) return true;

        return false;
    };

    const storeInLocalStorage = () => {
        const tradeInDeviceCensored = { ...tradeInDevice };

        delete tradeInDeviceCensored.GradeAPrice;
        delete tradeInDeviceCensored.GradeBPrice;

        if (!tradeInDeviceCensored) return;

        localStorage.setItem(STORAGEKEY_TRADE_IN_FORM_DATA, JSON.stringify({
            device: tradeInDeviceCensored,
            imei: tradeInDeviceIMEI,
            questionnaireAnswers,
            storage: page1Values.selectedStorage
        }));
    };

    const retrieveFromLocalStorage = () => {
        const data = localStorage.getItem(STORAGEKEY_TRADE_IN_FORM_DATA);

        if (!data) return;

        const parsedData = JSON.parse(data);

        setTradeInDevice(parsedData.device);
        setTradeInDeviceIMEI(parsedData.imei);
        setQuestionnaireAnswers(parsedData.questionnaireAnswers);
        setPage1Values({
            selectedCategory: parsedData.device.categoryName,
            selectedBrand: parsedData.device.brandName,
            selectedDevice: parsedData.device.name,
            selectedStorage: parsedData.storage
        });
    };

    const handleSubmit = (tradeInDeviceEstimates) => {
        const tradeInDeviceCensored = { ...tradeInDevice };

        delete tradeInDeviceCensored.GradeAPrice;
        delete tradeInDeviceCensored.GradeBPrice;

        storeInLocalStorage();
        localStorageSetTradeInDevice({
            productId: tradeInDevice.id,
            imei: tradeInDeviceIMEI,
            estimates: {
                id: tradeInDeviceEstimates.estimateId,
                total: tradeInDeviceEstimates.total
            },
            device: tradeInDeviceCensored,
            storage: page1Values.selectedStorage
        });
        toggle({ submitted: true });
        setPage(1);
    };

    const renderPage = (page) => {
        switch (page) {
            case 1:
                return <Page1 page1Values={page1Values} setPage1Values={setPage1Values} setTradeInDevice={setTradeInDevice} />;
            case 2:
                return <Page2 productId={tradeInDevice.id} questionnaireAnswers={questionnaireAnswers}
                    setQuestionnaireAnswers={setQuestionnaireAnswers} setDeviceConditionString={setDeviceConditionString} setProductAssessmentFormCompleted={setProductAssessmentFormCompleted} />;
            case 3:
                return <Page3 tradeInDeviceIMEI={tradeInDeviceIMEI} setTradeInDeviceIMEI={setTradeInDeviceIMEI} roughEstimate={roughEstimate}
                    deviceInfo={page1Values} isTncChecked={isTncChecked} setIsTncChecked={setIsTncChecked} />;
            default:
                return null;
        }
    };

    useEffect(() => {
        retrieveFromLocalStorage();
    }, []);

    return (
        <>
            <MUIButton variant='outlined' disableRipple={true} className={`${styles.ctaButton} trade-in__options option`}
                onClick={toggle}>
                <p>Yes, please</p>
                <div className={styles.divider} />
                <div className={styles.buttonRight}>
                    <small>Cashback up to</small>
                    <strong>SGD {maxTradeInValue} {isSeptPromoValid() && '+ 100'}</strong>
                </div>
            </MUIButton>
            <Modal isOpen={modal} toggle={toggle} size='xl'
                className={styles.tradeInFormModal}>
                <ModalBody className={styles.modalBody}>
                    <Button close={true} onClick={toggle} />
                    <div className='w-100'>
                        <h2>{titles[page]}</h2>
                        <Stepper page={page} />
                    </div>
                    {renderPage(page)}
                </ModalBody>
                <ModalFooter className={styles.modalFooter}>
                    <Button className={styles.buttonPrimaryOutline} onClick={toPreviousPage}>
                        {page > 1 ? 'Back' : 'Close'}
                    </Button>
                    <Button className={styles.buttonPrimary} onClick={toNextPage} disabled={disableCta()}>
                        {page < 3 ? 'Continue' : 'Finish'}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

const Stepper = ({ page }) => {
    const getClassName = (step) =>
        `${styles.stepperItem} ${page >= step ? styles.active : ''}`;

    return (
        <div className={styles.stepper}>
            <div className={getClassName(1)} />
            <div className={getClassName(2)} />
            <div className={getClassName(3)} />
        </div>
    );
};

export default TradeInFormModal;

TradeInFormModal.propTypes = {
    showTradeInModal: PropTypes.bool.isRequired,
    toggleTradeInModal: PropTypes.func.isRequired,
    selectedDurationTime: PropTypes.number.isRequired,
    setTradeInDevice: PropTypes.func.isRequired,
    product: PropTypes.object.isRequired,
    subtotal: PropTypes.number.isRequired,
    overtrade: PropTypes.bool.isRequired,
    maxTradeInValue: PropTypes.number
};

Stepper.propTypes = {
    page: PropTypes.number.isRequired
};
